// File: frontend/src/components/Calendar/WorkingHoursSettings.js
// UPDATE v1.1 (2025-11-06):
// - [ЗАДАЧА 43] РЕШЕНИЕ: Полная переработка для поддержки { hours, timezone }.
//   1. Заменен 'calendarService' на 'apiService' для унификации.
//   2. Добавлена константа TIMEZONE_OPTIONS.
//   3. Добавлено состояние [timezone, setTimezone] и [initialTimezone, setInitialTimezone].
//   4. loadWorkingHours (useEffect) теперь корректно парсит { hours, timezone } из data.
//   5. handleSave теперь отправляет { hours, timezone } в API.
//   6. Добавлен <select> для Timezone в JSX.
//   7. Добавлена кнопка "Reset" для отмены изменений.
//
// VERSION v1.0 (2025-10-25)
// - (Старая версия, вызывавшая белый экран)

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/apiService'; // (!!!) Используем apiService
import LoadingSpinner from '../LoadingSpinner'; // (!!!) Импортируем LoadingSpinner
import './WorkingHoursSettings.css';

// (!!!) [НОВЫЙ КОНСТАНТ v1.1]
const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona (MST)" },
  { value: "UTC", label: "UTC" },
];

// (!!!) [НОВЫЙ КОНСТАНТ v1.1]
const defaultHours = {
  monday: { start: '09:00', end: '17:00', isWorking: true },
  tuesday: { start: '09:00', end: '17:00', isWorking: true },
  wednesday: { start: '09:00', end: '17:00', isWorking: true },
  thursday: { start: '09:00', end: '17:00', isWorking: true },
  friday: { start: '09:00', end: '17:00', isWorking: true },
  saturday: { start: '09:00', end: '17:00', isWorking: false },
  sunday: { start: '09:00', end: '17:00', isWorking: false },
};

const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const dayLabels = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

// (!!!) [НОВЫЙ HELPER v1.1]
// Helper для генерации <option> времени (используем 30-мин шаги)
const TimeSelect = ({ value, onChange, disabled }) => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      options.push(<option key={time} value={time}>{time}</option>);
    }
  }
  return <select value={value} onChange={onChange} disabled={disabled} className="time-select">{options}</select>;
};


const WorkingHoursSettings = ({ isOpen, onClose, onSave }) => {
  const [workingHours, setWorkingHours] = useState(defaultHours);
  const [initialWorkingHours, setInitialWorkingHours] = useState(defaultHours);
  
  // (!!!) [НОВЫЕ СОСТОЯНИЯ v1.1]
  const [timezone, setTimezone] = useState('America/New_York');
  const [initialTimezone, setInitialTimezone] = useState('America/New_York');

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // (!!!) [ОБНОВЛЕННАЯ ЛОГИКА v1.1]
  // Проверяем изменения в часах ИЛИ часовом поясе
  const hasHoursChanged = JSON.stringify(workingHours) !== JSON.stringify(initialWorkingHours);
  const hasTimezoneChanged = timezone !== initialTimezone;
  const isSaveDisabled = isLoading || isSaving || (!hasHoursChanged && !hasTimezoneChanged);


  // (!!!) [ОБНОВЛЕНО v1.1]
  // Загружаем И часы, И часовой пояс
  const loadWorkingHours = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // 1. Используем apiService
      // 2. API теперь возвращает { hours: {...}, timezone: "..." }
      const data = await apiService.getWorkingHours();
      
      const loadedHours = data.hours || defaultHours;
      const loadedTimezone = data.timezone || 'America/New_York';

      // 3. Устанавливаем оба состояния
      setWorkingHours(loadedHours);
      setInitialWorkingHours(loadedHours); // Для кнопки "Reset"
      setTimezone(loadedTimezone);
      setInitialTimezone(loadedTimezone); // Для кнопки "Reset"

    } catch (err) {
      console.error('Error loading working hours:', err);
      setError('Failed to load working hours. Using defaults.');
      setWorkingHours(defaultHours);
      setTimezone('America/New_York');
    } finally {
      setIsLoading(false);
    }
  }, []); // useCallback

  // Загрузка рабочего времени при открытии модального окна
  useEffect(() => {
    if (isOpen) {
      loadWorkingHours();
    }
  }, [isOpen, loadWorkingHours]); // Добавляем loadWorkingHours

  const handleToggleWorkingDay = (day) => {
    setWorkingHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        isWorking: !prev[day].isWorking,
      },
    }));
  };

  const handleTimeChange = (day, field, value) => {
    setWorkingHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
  };

  // (!!!) [ОБНОВЛЕНО v1.1]
  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      
      // 1. Создаем объект, который ожидает API
      const dataToSave = {
        hours: workingHours,
        timezone: timezone
      };

      // 2. Используем apiService
      const updatedData = await apiService.updateWorkingHours(dataToSave);
      
      // 3. Обновляем "initial" состояния
      setInitialWorkingHours(updatedData.hours);
      setWorkingHours(updatedData.hours);
      setInitialTimezone(updatedData.timezone);
      setTimezone(updatedData.timezone);
      
      toast.success('Availability updated successfully!');
      
      if (onSave) {
        onSave(updatedData); // Отправляем полные данные обратно
      }
      
      onClose();
    } catch (err) {
      console.error('Error saving working hours:', err);
      setError(err.message || 'Failed to save working hours');
      toast.error(err.message || 'Failed to save working hours');
    } finally {
      setIsSaving(false);
    }
  };

  // (!!!) [ОБНОВЛЕНО v1.1]
  const handleReset = () => {
    setWorkingHours(initialWorkingHours);
    setTimezone(initialTimezone);
    toast('Changes have been reset.');
  };

  // (!!!) [ОБНОВЛЕНО v1.1]
  // Заменяем 'Reset to Defaults' на 'Reset Changes'
  const handleResetToDefaults = () => {
    if (window.confirm('Reset to default working hours (9 AM - 5 PM)?')) {
      setWorkingHours(defaultHours);
      setTimezone('America/New_York');
      toast.success('Hours reset to default.');
    }
  };


  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content working-hours-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>My Availability Settings</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {isLoading ? (
          // (!!!) Используем LoadingSpinner
          <LoadingSpinner message="Loading availability..." />
        ) : (
          <div className="working-hours-container">
            
            {/* --- (!!!) [НОВОЕ ПОЛЕ v1.1] (Timezone) --- */}
            <div className="form-group timezone-select-group">
                <label htmlFor="modal-timezone">My Timezone</label>
                <select 
                  id="modal-timezone" 
                  value={timezone} 
                  onChange={(e) => setTimezone(e.target.value)} 
                  disabled={isLoading || isSaving}
                  className="timezone-select"
                >
                    {TIMEZONE_OPTIONS.map(tz => (
                        <option key={tz.value} value={tz.value}>
                            {tz.label} ({tz.value})
                        </option>
                    ))}
                </select>
            </div>
            {/* --- [КОНЕЦ v1.1] --- */}

            <p className="working-hours-info">Set the times you are available. Clients can only book appointments during these times.</p>

            <div className="working-hours-list">
              {daysOfWeek.map((day) => (
                <div key={day} className={`working-hours-item ${!workingHours[day].isWorking ? 'day-disabled' : ''}`}>
                  <div className="day-header">
                    <label className="day-label">
                      <input
                        type="checkbox"
                        checked={workingHours[day].isWorking}
                        onChange={() => handleToggleWorkingDay(day)}
                        disabled={isSaving}
                      />
                      <span className="day-name">{dayLabels[day]}</span>
                    </label>
                  </div>

                  {workingHours[day].isWorking ? (
                    <div className="time-inputs">
                      {/* (!!!) Используем новый TimeSelect (30-мин) */}
                      <TimeSelect
                        value={workingHours[day].start}
                        onChange={(e) => handleTimeChange(day, 'start', e.target.value)}
                        disabled={isSaving}
                      />
                      <span className="time-separator">to</span>
                      <TimeSelect
                        value={workingHours[day].end}
                        onChange={(e) => handleTimeChange(day, 'end', e.target.value)}
                        disabled={isSaving}
                      />
                    </div>
                  ) : (
                     <div className="day-off-label">Unavailable</div>
                  )}
                </div>
              ))}
            </div>

            <div className="working-hours-actions-defaults">
              <button
                type="button"
                className="btn btn-link"
                onClick={handleResetToDefaults}
              >
                Reset to Defaults
              </button>
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isSaving || isLoading}
          >
            Cancel
          </button>
          
          {/* (!!!) [НОВАЯ КНОПКА v1.1] */}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleReset}
            disabled={isSaveDisabled}
          >
            Reset Changes
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaveDisabled || isSaving || isLoading} // (!!!) Добавил isSaveDisabled
          >
            {isSaving ? <LoadingSpinner size="sm" /> : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkingHoursSettings;